import { leafFilter, requestFilterTree } from 'api/filter-tree'
import { ACCOUNT_SEGMENT_PARAM_KEYS, getColumnDisplayName } from 'dashboard/utils'
import { customColumnFilterColumn, filterableCustomColumnTypes } from 'shared/column-filters/custom-column-filter'
import { choiceVerb, describeFilter } from './summary'

const LISTED = 5

// Long lists show their first few values; the rest are counted.
const clause = (verb, values = []) => ({
  verb,
  values: values.slice(0, LISTED),
  more: Math.max(values.length - LISTED, 0)
})

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`

// What a leaf built from these values would send, as clauses; empty when the column isn't filtered.
const columnClauses = (params, serializeWith, column) => {
  const pruned = requestFilterTree({ filter: leafFilter(params, serializeWith) })
  return pruned ? describeFilter(pruned.filter, column).map(({ verb, values }) => clause(verb, values)) : []
}

const includeExclude = (records, nameOf) => {
  const include = records.filter((record) => record.options !== 'exclude').map(nameOf)
  const exclude = records.filter((record) => record.options === 'exclude').map(nameOf)
  return [
    include.length && clause(choiceVerb(include.length), include),
    exclude.length && clause(choiceVerb(exclude.length, true), exclude)
  ].filter(Boolean)
}

// Every filter a View applies outside its AND/OR groups, read from the same params its grid
// and charts build their requests from. The API ANDs each of these with the groups.
export const appliedViewFilters = ({ params, columns = [], customColumns = [], municipality = null }) => {
  const columnNamed = (databaseColumnName) => columns.find((c) => c.database_column_name === databaseColumnName)
  const labelOf = (column, fallback) => (column ? getColumnDisplayName(column, municipality) : fallback)
  const applied = []

  columns
    .filter((column) => column.accessor_key && column.filter_type && column.filter_type !== 'reference')
    .forEach((column) => {
      const groups = (params[column.accessor_key] || []).length
      const clauses =
        column.filter_type === 'group_multiselect'
          ? groups
            ? [clause(`${params[`${column.accessor_key}Mode`] === 'exclude' ? 'excludes' : 'includes'} ${plural(groups, 'group')}`)]
            : []
          : columnClauses(params, { columns: [column] }, column)
      if (clauses.length) applied.push({ key: column.accessor_key, label: labelOf(column), clauses })
    })

  customColumns
    .filter((customColumn) => filterableCustomColumnTypes.includes(customColumn.column_type))
    .forEach((customColumn) => {
      const filterColumn = customColumnFilterColumn(customColumn)
      const clauses = columnClauses(params, { customColumns: [customColumn] }, filterColumn)
      if (clauses.length) applied.push({ key: filterColumn.accessor_key, label: filterColumn.name, clauses })
    })

  const vendors = [...(params.vendors || []), ...(params.excludedVendors || []).map((v) => ({ ...v, options: 'exclude' }))]
  if (vendors.length) {
    applied.push({ key: 'vendors', label: 'Vendor', clauses: includeExclude(vendors, (v) => v.name || v.external_id || v.id) })
  }

  const employeeColumns = [...new Set((params.employees || []).map((e) => e.database_column_name).filter(Boolean))]
  employeeColumns.forEach((databaseColumnName) => {
    const employees = params.employees.filter((e) => e.database_column_name === databaseColumnName)
    applied.push({
      key: `employees-${databaseColumnName}`,
      label: labelOf(columnNamed(databaseColumnName), databaseColumnName),
      clauses: includeExclude(employees, (e) => e.display_name || e.full_name || e.external_id)
    })
  })

  // A View's analytics are each required (or each ruled out), not alternatives.
  const analytics = params.analytics || []
  if (analytics.length) {
    const has = analytics.filter((a) => a.options !== 'exclude').map((a) => a.name || a.id)
    const lacks = analytics.filter((a) => a.options === 'exclude').map((a) => a.name || a.id)
    applied.push({
      key: 'analytics',
      label: 'Analytics',
      clauses: [
        has.length && clause(has.length > 1 ? 'flagged by all of' : 'flagged by', has),
        lacks.length && clause(lacks.length > 1 ? 'not flagged by any of' : 'not flagged by', lacks)
      ].filter(Boolean)
    })
  }

  Object.entries(ACCOUNT_SEGMENT_PARAM_KEYS).forEach(([databaseColumnName, paramKey]) => {
    const ids = (params[paramKey] || []).filter(Boolean)
    if (!ids.length) return
    const verb = params[`${paramKey}Operator`] === 'exclude' ? 'excludes' : 'includes'
    applied.push({
      key: paramKey,
      label: labelOf(columnNamed(databaseColumnName), databaseColumnName),
      clauses: [clause(`${verb} ${plural(ids.length, 'code')}`)]
    })
  })

  const bounds = [
    params.minimum_amount && clause('≥', [String(params.minimum_amount)]),
    params.maximum_amount && clause('≤', [String(params.maximum_amount)])
  ].filter(Boolean)
  if (bounds.length) applied.push({ key: 'amount', label: 'Amount', clauses: bounds })

  return applied
}
